import { useCallback, useEffect, useLayoutEffect, useRef } from "react";

/**
 * Measures a content element's INTRINSIC height and shrinks a CSS custom
 * property (`--fit-<varName>`, paired in CSS with
 * `transform: scale(var(...))` + `width: calc(100% / var(...))`) until it
 * provably fits inside `containerRef`'s padded box. Re-measures on every
 * resize and every dependency change, so panels with variable-length
 * content (translated strings, a scenario author adding more list items,
 * etc.) can never overflow into a scrollbar regardless of window size --
 * see WifiKiosk.tsx's local original of this hook for the terminal-screen
 * case this pattern was born from (2026-07-15).
 */
export function useFitScale<T extends HTMLElement = HTMLDivElement, C extends HTMLElement = HTMLDivElement>(
  varName: string,
  deps: readonly unknown[],
) {
  const containerRef = useRef<T | null>(null);
  const contentRef = useRef<C | null>(null);
  const prop = `--fit-${varName}`;

  const measure = useCallback(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) return;
    const cs = getComputedStyle(container);
    const available =
      container.clientHeight - (parseFloat(cs.paddingTop) || 0) - (parseFloat(cs.paddingBottom) || 0);
    if (available <= 0) return;

    const prevFlex = content.style.flex;
    const prevHeight = content.style.height;
    content.style.flex = "0 0 auto";
    content.style.height = "auto";

    let s = 1;
    for (let i = 0; i < 26; i++) {
      content.style.setProperty(prop, String(s));
      const naturalHeight = content.scrollHeight;
      if (naturalHeight * s <= available + 1 || s <= 0.5) break;
      s = Math.max(0.5, Math.round((s - 0.025) * 1000) / 1000);
    }

    content.style.flex = prevFlex;
    content.style.height = prevHeight;
  }, [prop]);

  useLayoutEffect(() => {
    contentRef.current?.style.setProperty(prop, "1");
    measure();
    const raf = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const ro = new ResizeObserver(() => measure());
    ro.observe(container);
    return () => ro.disconnect();
  }, [measure]);

  return { containerRef, contentRef };
}
